import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { UI_PREFS_KEYS, getUiPref, setUiPref } from './utils/browserStorage';

export const THEMES = { dark: 'dark', light: 'light' };
const DEFAULT_THEME = THEMES.dark;

const normalizeTheme = (value) => (value === THEMES.light ? THEMES.light : THEMES.dark);

/** Reads the persisted theme (falls back to the default dark theme). */
const readStoredTheme = () => {
  try {
    return normalizeTheme(getUiPref(UI_PREFS_KEYS.theme));
  } catch {
    return DEFAULT_THEME;
  }
};

/** Reflects the theme onto <html data-theme> so CSS variables switch instantly. */
export const applyThemeAttribute = (theme) => {
  if (typeof document === 'undefined') { return; }
  document.documentElement.setAttribute('data-theme', normalizeTheme(theme));
};

// Apply as early as possible (module import) to avoid a flash of the wrong theme.
applyThemeAttribute(readStoredTheme());

const ThemeContext = createContext({
  theme: DEFAULT_THEME,
  setTheme: () => {},
  toggleTheme: () => {},
});

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(readStoredTheme);

  useEffect(() => {
    applyThemeAttribute(theme);
    setUiPref(UI_PREFS_KEYS.theme, theme);
  }, [theme]);

  const setTheme = useCallback((next) => {
    setThemeState(normalizeTheme(next));
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((current) => (current === THEMES.light ? THEMES.dark : THEMES.light));
  }, []);

  const value = useMemo(() => ({ theme, setTheme, toggleTheme }), [theme, setTheme, toggleTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);
